import * as Yup from "yup";
import { QueryClient, QueryClientProvider, useMutation, useQuery } from "@tanstack/react-query";
import { useContext, useMemo } from "react";
import { styled } from "@mui/material";
import { Field, FormikProvider, useFormik } from "formik";
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import Select from "../../../../customs/SelectInput";
import { NotificationContext } from "../../../../providers/notification";
import apiCall from "../../../../utils/apiCall";
import { useQueryClient } from "react-query";

interface Props {
  RouteId: string;
  RouteName: string
  StateDate: string;
  endDate: string;
  year: number;
  route: number;
  status: boolean;
  periodId: number;
  handleClose: () => void;
}

interface Route {
  RouteId: number;
  RouteName: string;
  Status: boolean;
  StateDate: string;
  EndDate: string;

}


interface RouteResponse {
  StatusCode: number;
  Message: string;
  Data: Route[];
}


const formatDate = (dateString: string) =>
  new Date(dateString).toISOString().split("T")[0];

const getAllRoute = async (): Promise<RouteResponse> => {
  return (await apiCall().get("/Routes/GetAllRoute")).data;
};


const UpdateApplicationPeriod: React.FC<Props> = ({
  StateDate,
  endDate,
  year,
  status,
  RouteId,
  RouteName,
  periodId,
  handleClose,
}) => {
  const queryClient = useQueryClient();
  const { showNotification } = useContext(NotificationContext);
  
  const { data: allRoutesResponse } = useQuery(["get-all-route"], getAllRoute);
  const allRoutes = allRoutesResponse?.Data || [];
  
let selectedRouteName = "N/A";

if (RouteId && allRoutes.length > 0) {
  const matched = allRoutes.find(
    (route) => Number(route.RouteId) === Number(RouteId)
  );

  if (matched) {
    selectedRouteName = matched.RouteName;
  }

  console.log("Matched Route:", matched); // ✅ DEBUG
}





 const validationSchema = Yup.object().shape({
  StateDate: Yup.date().required("Start Date is required"),
  endDate: Yup.date().required("End Date is required"),
  Year: Yup.number().required("Year is required"),
  Status: Yup.mixed().oneOf(["true", "false"]).required("Status is required"),});


 const updateApplicationPeriodMutation = useMutation({
  mutationFn: (data: any) =>
    apiCall().post("/SetUp/UpdateApplicationPeriod", data),
  retry: 0,
});

const formik = useFormik({
  enableReinitialize: true,
  initialValues: {
  RouteId: RouteId ? Number(RouteId) : "",
  RouteName: RouteName ,
  StateDate: formatDate(StateDate),
  endDate: formatDate(endDate),
  Year: year || 0,
  Status: status ? "true" : "false", // form uses string but API expects boolean, so convert on submit
},                 
  validationSchema,
  onSubmit: async (values, { resetForm }) => {
   const payload = {
  ApplicationPeriodId: periodId,
  RouteId: Number(values.RouteId),
  StateDate: values.StateDate,
  RouteName: values.RouteName,
  EndDate: values.endDate,
  Year: Number(values.Year),
  Status: values.Status === "true", // convert to boolean
};
       

    try {
    await updateApplicationPeriodMutation.mutateAsync(payload);

        showNotification({
          message: "Application Period successfully updated",
          type: "success",
        });

        await queryClient.invalidateQueries(["get-application-periods"]);
        handleClose();
        resetForm();

    } catch (error: any) {
      const message =
        error?.response?.data?.title ||
        error?.response?.data?.message ||
        error?.message ||
        "An unexpected error occurred";

      showNotification({
        message,
        type: "error",
      });
    }
  },
});


  const selectOptions = (
    <>
      <option value="true">Yes</option>
      <option value="false">No</option>
    </>
  );

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <FullWidthWrapper>
         <Input
            name="RouteName"
            label="Route Name"
            value={selectedRouteName}
            readOnly
          />

        </FullWidthWrapper>

        <StyledInputWrapper>
          <Field
            name="StateDate"
            type="date"
            as={Input}
            label="Start Date"
            placeholder="Start Date"
          />
          <Field
            name="endDate"
            type="date"
            as={Input}
            label="End Date"
            placeholder="End Date"
          />
          <Field
            name="Year"
            as={Input}
            label="Year"
            placeholder="e.g. 2025"
          />
          <Field
          name="Status"
          as={Select}
          label="Is Active?"
          options={
            <>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </>
          }
        />
        </StyledInputWrapper>

        <ButtonWrapper>
          <ButtonContainer>
            <StyledButton text="Cancel" type="button" onClick={handleClose} />
            <StyledButton
              text={
                updateApplicationPeriodMutation.isLoading
                  ? "Submitting..."
                  : "Submit"
              }
              type="submit"
              disabled={updateApplicationPeriodMutation.isLoading}
            />
          </ButtonContainer>
        </ButtonWrapper>
      </form>
    </FormikProvider>
  );
};

export default UpdateApplicationPeriod;

// ======= STYLES ======= //

const StyledInputWrapper = styled("div")`
  display: grid;
  flex-flow: row wrap;
  align-items: start;
  column-gap: 4rem;
  margin-inline: 4rem;

  @media (min-width: 1038px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 587px) {
    justify-content: center;
    grid-template-columns: repeat(1, 1fr);
  }
`;

const ButtonWrapper = styled("div")`
  display: flex;
  justify-content: flex-end;
  margin-block: 2.5rem;
  padding-inline: 3.5rem;
`;

const ButtonContainer = styled("div")`
  display: flex;
  gap: 2rem;
`;

const StyledButton = styled(Button)`
  padding-block: 1rem;
  padding-inline: 2rem;
`;

const FullWidthWrapper = styled("div")`
  width: 80%;
  margin: 0 4rem 2rem 4rem;
`;
